import * as React from 'react';

import { toCountFormat, toJutaFormat, toPercentageFormat } from './Dashboard.formatters';
import { BranchPerformanceMetric, DashboardBranchPerformance } from './Dashboard.queries';

interface BranchPerformanceSectionProps {
  branchPerformance: DashboardBranchPerformance[];
  metric: BranchPerformanceMetric;
  onMetricChange: (metric: BranchPerformanceMetric) => void;
}

interface MetricToggleProps {
  isActive: boolean;
  label: string;
  metric: BranchPerformanceMetric;
  onSelect: (metric: BranchPerformanceMetric) => void;
}

const METRIC_OPTIONS: Array<{ label: string; metric: BranchPerformanceMetric }> = [
  { label: 'By Revenue', metric: BranchPerformanceMetric.REVENUE },
  { label: 'By Orders', metric: BranchPerformanceMetric.ORDERS },
  { label: 'By Volume', metric: BranchPerformanceMetric.VOLUME },
  { label: 'By On-Time Rate', metric: BranchPerformanceMetric.ON_TIME_RATE }
];

const TOP_RANK_LIMIT = 3;

class MetricToggle extends React.PureComponent<MetricToggleProps> {
  render() {
    const { isActive, label } = this.props;
    return (
      <button
        className={`Dashboard__toggle${isActive ? ' Dashboard__toggle--active' : ''}`}
        onClick={this.handleClick}
        type="button"
      >
        {label}
      </button>
    );
  }

  private readonly handleClick = () => this.props.onSelect(this.props.metric);
}

const getMetricValue = (branch: DashboardBranchPerformance, metric: BranchPerformanceMetric): number => {
  switch (metric) {
    case BranchPerformanceMetric.ORDERS:
      return branch.orders;
    case BranchPerformanceMetric.VOLUME:
      return branch.volume;
    case BranchPerformanceMetric.ON_TIME_RATE:
      return branch.onTimeRate;
    default:
      return branch.revenue;
  }
};

export const BranchPerformanceSection = (props: BranchPerformanceSectionProps) => {
  const { branchPerformance, metric, onMetricChange } = props;

  const sorted = [...branchPerformance].sort(
    (a: DashboardBranchPerformance, b: DashboardBranchPerformance) =>
      getMetricValue(b, metric) - getMetricValue(a, metric)
  );
  const maxValue = sorted.length > 0 ? getMetricValue(sorted[0], metric) : 0;

  return (
    <div className="Dashboard__card">
      <div className="Dashboard__card-header">
        <div>
          <h3 className="Dashboard__card-title">Branch Performance</h3>
          <p className="Dashboard__card-subtitle">Performance by branch based on selected period</p>
        </div>
        <div className="Dashboard__toggle-group">
          {METRIC_OPTIONS.map((option: { label: string; metric: BranchPerformanceMetric }) => (
            <MetricToggle
              isActive={option.metric === metric}
              key={option.metric}
              label={option.label}
              metric={option.metric}
              onSelect={onMetricChange}
            />
          ))}
        </div>
      </div>

      {sorted.length === 0 && <div className="Dashboard__empty">No branch data in this period</div>}

      {sorted.length > 0 && (
        <div className="Dashboard__table-scroll">
          <table className="Dashboard__table">
            <thead>
              <tr>
                <th className="Dashboard__th Dashboard__th--rank">RANK</th>
                <th className="Dashboard__th">BRANCH</th>
                <th className="Dashboard__th Dashboard__th--bar">
                  {METRIC_OPTIONS.filter(
                    (option: { label: string; metric: BranchPerformanceMetric }) => option.metric === metric
                  )[0].label.replace('By ', '').toUpperCase()}
                </th>
                <th className="Dashboard__th Dashboard__th--num">REVENUE</th>
                <th className="Dashboard__th Dashboard__th--num">ORDERS</th>
                <th className="Dashboard__th Dashboard__th--num">VOLUME</th>
                <th className="Dashboard__th Dashboard__th--num">ON-TIME RATE</th>
              </tr>
            </thead>
            <tbody>
              {sorted.map((branch: DashboardBranchPerformance, index: number) => {
                const value = getMetricValue(branch, metric);
                const barWidth = maxValue > 0 ? (value / maxValue) * 100 : 0;
                const rank = index + 1;

                return (
                  <tr key={branch.branchId}>
                    <td className="Dashboard__td Dashboard__td--rank">
                      <span
                        className={`Dashboard__rank${
                          rank <= TOP_RANK_LIMIT ? ` Dashboard__rank--top${rank}` : ''
                        }`}
                      >
                        {rank}
                      </span>
                    </td>
                    <td className="Dashboard__td Dashboard__td--branch">{branch.branchName}</td>
                    <td className="Dashboard__td">
                      <span className="Dashboard__bar-track">
                        <span className="Dashboard__bar-fill" style={{ width: `${barWidth}%` }} />
                      </span>
                    </td>
                    <td className="Dashboard__td Dashboard__td--num">{toJutaFormat(branch.revenue)}</td>
                    <td className="Dashboard__td Dashboard__td--num">{toCountFormat(branch.orders)}</td>
                    <td className="Dashboard__td Dashboard__td--num">{toCountFormat(branch.volume)}</td>
                    <td className="Dashboard__td Dashboard__td--num Dashboard__td--rate">
                      {/* No completed deliveries is not the same as 0% on-time. */}
                      {branch.finishedOrders > 0 ? toPercentageFormat(branch.onTimeRate) : '–'}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};
